import { DEFAULT_LOCALE, resolveLocaleFallbackOrNull } from './handler';

export const LOCALE_QUERY_KEY = 'locale';

const hasOwnLocale = (query) => Object.prototype.hasOwnProperty.call(query, LOCALE_QUERY_KEY);

const firstQueryValue = (value) => (Array.isArray(value) ? value[0] : value);

const getCanonicalLocale = (locale, fallbackLocale = DEFAULT_LOCALE) => (
  resolveLocaleFallbackOrNull(locale)?.fullLocale || fallbackLocale
);

const decodeComponents = (components, splitIndex = 1) => {
  try {
    return [decodeURIComponent(components.join(''))];
  } catch (_error) {
    if (components.length === 1) {
      return components;
    }

    return [
      ...decodeComponents(components.slice(0, splitIndex)),
      ...decodeComponents(components.slice(splitIndex)),
    ];
  }
};

const decodeMalformedQueryValue = (value) => {
  const replacementMap = {
    '%FE%FF': '��',
    '%FF%FE': '��',
  };
  const encodedSequences = value.match(/(%[a-f0-9]{2})+/gi) || [];

  encodedSequences.forEach((encodedSequence) => {
    try {
      replacementMap[encodedSequence] = decodeURIComponent(encodedSequence);
    } catch (_error) {
      let decodedSequence = encodedSequence;
      let components = encodedSequence.match(/(%[a-f0-9]{2})|([^%]+?)/gi) || [];
      for (let splitIndex = 1; splitIndex < components.length; splitIndex += 1) {
        decodedSequence = decodeComponents(components, splitIndex).join('');
        components = decodedSequence.match(/(%[a-f0-9]{2})|([^%]+?)/gi) || [];
      }
      if (decodedSequence !== encodedSequence) {
        replacementMap[encodedSequence] = decodedSequence;
      }
    }
  });

  replacementMap['%C2'] = '�';
  return Object.keys(replacementMap).reduce(
    (decodedValue, encodedValue) => decodedValue.replace(
      new RegExp(encodedValue, 'g'),
      replacementMap[encodedValue],
    ),
    value,
  );
};

const decodeQueryValue = (value) => {
  const normalizedValue = value.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(normalizedValue);
  } catch (_error) {
    return decodeMalformedQueryValue(normalizedValue);
  }
};

const encodeQueryValue = (value) => encodeURIComponent(value).replace(
  /[!'()*]/g,
  (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
);

const getParameterSeparatorIndex = (parameter) => parameter.indexOf('=');

const decodeParameterKey = (parameter, separatorIndex) => decodeQueryValue(
  separatorIndex === -1 ? parameter : parameter.slice(0, separatorIndex),
);

const decodeParameterValue = (parameter, separatorIndex) => (
  separatorIndex === -1 ? null : decodeQueryValue(parameter.slice(separatorIndex + 1))
);

const decodeParameter = (parameter) => {
  const separatorIndex = getParameterSeparatorIndex(parameter);
  return {
    key: decodeParameterKey(parameter, separatorIndex),
    value: decodeParameterValue(parameter, separatorIndex),
  };
};

const getFirstLocaleValue = (search) => {
  const normalizedSearch = search.trim().replace(/^[?#&]/, '');
  if (!normalizedSearch) {
    return { found: false, value: null };
  }

  const parameters = normalizedSearch.split('&');
  for (let index = 0; index < parameters.length; index += 1) {
    const parameter = parameters[index];
    const separatorIndex = getParameterSeparatorIndex(parameter);
    if (decodeParameterKey(parameter, separatorIndex) === LOCALE_QUERY_KEY) {
      return { found: true, value: decodeParameterValue(parameter, separatorIndex) };
    }
  }
  return { found: false, value: null };
};

/**
 * Parse a URL search string using the same semantics as Farce query middleware.
 * @param {string} search URL search string.
 * @returns {object} parsed query.
 */
export const parseSearch = (search = '') => {
  const query = Object.create(null);
  const normalizedSearch = search.trim().replace(/^[?#&]/, '');
  if (!normalizedSearch) {
    return query;
  }

  normalizedSearch.split('&').forEach((parameter) => {
    const { key, value } = decodeParameter(parameter);
    if (Object.prototype.hasOwnProperty.call(query, key)) {
      if (Array.isArray(query[key])) {
        query[key].push(value);
      } else {
        query[key] = [query[key], value];
      }
    } else {
      query[key] = value;
    }
  });
  const sortedQuery = Object.create(null);
  Object.keys(query).sort().forEach((key) => {
    sortedQuery[key] = query[key];
  });
  return sortedQuery;
};

/**
 * Serialize a query object as a URL search string.
 * @param {object} query query values.
 * @returns {string} serialized search including a leading question mark.
 */
export const stringifyQuery = (query = {}) => {
  const search = Object.keys(query).sort().flatMap((key) => {
    const value = query[key];
    if (value === undefined) {
      return [];
    }

    return (Array.isArray(value) ? value : [value]).flatMap((queryValue) => {
      if (queryValue === undefined) {
        return [];
      }
      const encodedKey = encodeQueryValue(key);
      return queryValue === null
        ? encodedKey
        : `${encodedKey}=${encodeQueryValue(queryValue)}`;
    });
  }).join('&');
  return search ? `?${search}` : '';
};

/**
 * Build a destination query while preserving only the current explicit locale.
 * @param {object} options options.
 * @param {string} options.currentSearch current browser search.
 * @param {object} options.destinationQuery destination query.
 * @returns {object} canonical destination query.
 */
export const buildLocaleQuery = ({ currentSearch = '', destinationQuery = {} }) => {
  const nextQuery = { ...destinationQuery };
  const destinationValue = firstQueryValue(nextQuery[LOCALE_QUERY_KEY]);
  const hasDestinationLocale = hasOwnLocale(nextQuery) && destinationValue !== undefined;

  delete nextQuery[LOCALE_QUERY_KEY];
  if (hasDestinationLocale) {
    if (destinationValue !== null && destinationValue !== '') {
      nextQuery[LOCALE_QUERY_KEY] = getCanonicalLocale(destinationValue);
    }
    return nextQuery;
  }

  const currentLocale = getFirstLocaleValue(currentSearch);
  if (currentLocale.found && currentLocale.value !== null && currentLocale.value !== '') {
    nextQuery[LOCALE_QUERY_KEY] = getCanonicalLocale(currentLocale.value);
  }
  return nextQuery;
};

/**
 * Canonicalize an explicit bootstrap locale without adding a configured-only locale.
 * @param {string} search current URL search.
 * @param {string} _resolvedFullLocale retained for API compatibility.
 * @returns {string} canonical search.
 */
export const canonicalizeExplicitLocaleSearch = (search, _resolvedFullLocale) => {
  const normalizedSearch = search.replace(/^[?#&]/, '');
  if (!normalizedSearch) {
    return search;
  }

  let hasExplicitLocale = false;
  const parameters = [];
  normalizedSearch.split('&').forEach((parameter) => {
    const separatorIndex = getParameterSeparatorIndex(parameter);
    const key = decodeParameterKey(parameter, separatorIndex);
    if (key !== LOCALE_QUERY_KEY) {
      parameters.push(parameter);
    } else if (!hasExplicitLocale) {
      hasExplicitLocale = true;
      const value = decodeParameterValue(parameter, separatorIndex);
      if (value !== null && value !== '') {
        parameters.push(`${LOCALE_QUERY_KEY}=${encodeQueryValue(getCanonicalLocale(value))}`);
      }
    }
  });

  if (!hasExplicitLocale) {
    return search;
  }
  return parameters.length ? `?${parameters.join('&')}` : '';
};

/**
 * Canonicalize the browser URL before Farce reads its initial location.
 * @param {string} resolvedFullLocale active bootstrap locale.
 * @param {object} browser browser location and history APIs.
 * @returns {boolean} true when the URL was replaced.
 */
export const canonicalizeInitialLocaleUrl = (
  resolvedFullLocale,
  browser = { history: window.history, location: window.location },
) => {
  const { history, location } = browser;
  const search = canonicalizeExplicitLocaleSearch(location.search, resolvedFullLocale);
  if (search === location.search) {
    return false;
  }

  history.replaceState(
    history.state,
    '',
    `${location.pathname}${search}${location.hash}`,
  );
  return true;
};

/**
 * Adds the current explicit locale to a same-origin URL without touching external URLs.
 * @param {string} destination destination URL.
 * @param {Location|URL} currentLocation current browser location.
 * @returns {string} destination with inherited locale when applicable.
 */
export const localizeSameOriginUrl = (destination, currentLocation = window.location) => {
  const currentHref = currentLocation.href || [
    window.location.origin,
    currentLocation.pathname || '/',
    currentLocation.search || '',
    currentLocation.hash || '',
  ].join('');
  const currentUrl = new URL(currentHref);
  const destinationUrl = new URL(destination, currentUrl);
  if (destinationUrl.origin !== currentUrl.origin) {
    return destination;
  }

  const query = buildLocaleQuery({
    currentSearch: currentUrl.search,
    destinationQuery: parseSearch(destinationUrl.search),
  });
  return `${destinationUrl.pathname}${stringifyQuery(query)}${destinationUrl.hash}`;
};
